import React, {
  FunctionComponent,
  useState,
  ChangeEvent,
  useEffect,
  useContext,
  MouseEvent,
} from "react";
import stickybits from "stickybits";
import { AppPage, FocusableHeader, LoadingMask } from "../../components";
import { Link } from "react-router-dom";
import routes from "../../app/routes";
import { TextField, Button } from "@cmsgov/ds-medicare-gov";
import { getInNetworkPharmacies, getPharmacies, getPlan } from "../../api";
import { AppStore } from "../../app/store";
import {
  ActionType,
  PharmacyType,
  PharmaciesRequest,
  LocationWithNextRouteInState,
  InNetworkPharmaciesResponse,
} from "../../@types";
import {
  useTranslate,
  IntlContext,
  CONSTANTS,
  sendTealiumEvent,
  parsePlanLongIdIntoUrlPartForApi,
  useYearInTitle,
} from "../../helpers";
import { useLocation } from "react-router";
import { createGa4Event, Ga4Event } from "../../helpers/analytics";
import { useFlags } from "launchdarkly-react-client-sdk";
import { buildAddress, updatePharmacyNetworkStatuses } from "./helpers";
import { PharmacySelectionFooter, ResultsContainer } from "./components";
import {
  InNetworkSelectPharmaciesPlanParams,
  PharmacyResultsType,
  PlanDetailsState,
  PlanDetailsStatus,
  SelectPharmaciesPageProps,
  ToggledFrom,
} from "./types";
import { getPlanName } from "../../helpers/planHelpers";

export const SelectPharmaciesPage: FunctionComponent<SelectPharmaciesPageProps> = ({
  isInNetwork = false,
  match,
}) => {
  const { dispatch, state } = useContext(AppStore.AppContext);
  const [planDetailState, setPlanDetailState] = React.useState<
    PlanDetailsState
  >({
    status: PlanDetailsStatus.None,
    plan: undefined,
  });
  const {
    zipcode,
    pharmacies,
    pharmacyType,
    pharmacyAddress,
    mbpReferrerUri,
  } = state;
  const flags = useFlags();
  const { mctCurrentPlanYear = "" } = useFlags();
  const DEFAULT_SEARCH_DISTANCE = 10;
  const location = useLocation();
  const [pharmacyNameSearch, setPharmacyNameSearch] = useState("");
  const [previousNameSearch, setPreviousNameSearch] = useState("");
  const [mailOrder, setMailOrder] = useState(
    [PharmacyType.MAIL, PharmacyType.MAIL_AND_RETAIL].includes(pharmacyType)
  );
  const [mailOrderNetworkStatus, setMailOrderNetworkStatus] = useState({
    isMailOrderInNetwork: false,
    isMailOrderPreferred: false,
    shouldMailOrderBeInResults: true,
  });
  const [addressSearch, setAddressSearch] = useState(pharmacyAddress || "");
  const [pharmacyResults, setPharmacyResults] = useState<
    PharmacyResultsType | undefined
  >();

  const [page, setPage] = useState(0);
  const [planId, setPlanId] = useState("");
  const [searchDistance, setSearchDistance] = useState(DEFAULT_SEARCH_DISTANCE);
  const [loading, setLoading] = useState(true);
  const [totalPharmacies, setTotalPharmacies] = useState(0);
  const [savedPharmacies, setSavedPharmacies] = useState(pharmacies);
  const [totalPages, setTotalPages] = useState(1);
  const [showMap, setShowMap] = useState(false);
  const [showMobileFilter, setShowMobileFilter] = useState(false);
  const [showOnlyPreferred, setShowOnlyPreferred] = useState(false);
  const [mobileSearchDistance, setMobileSearchDistance] = useState<
    number | undefined
  >();
  const t = useTranslate();
  const fm = useContext(IntlContext).formatMessage;
  const [footerMessage, setFooterMessage] = useState("");
  const [nextRoute, setNextRoute] = useState<typeof location.state>();
  const hasMbpReferrerUri = mbpReferrerUri !== "";
  const {
    state: { lis, year, futureLis },
  } = React.useContext(AppStore.AppContext);
  const pharmacyAnchorTarget = "pharmacy-selection-footer-sticky-tray-heading";

  useEffect(() => {
    showMapBasedOnResize();

    window.addEventListener("resize", showMapBasedOnResize);

    if (isInNetwork) {
      const currentPlanId = parsePlanLongIdIntoUrlPartForApi(
        (match.params as InNetworkSelectPharmaciesPlanParams).id
      );
      setPlanId(currentPlanId);
      if (
        currentPlanId &&
        planDetailState.status !== PlanDetailsStatus.Loading
      ) {
        fetchPlan(currentPlanId);
      }
    }

    return (): void =>
      window.removeEventListener("resize", showMapBasedOnResize);
  }, []);

  useEffect(() => {
    if (location.state) {
      const locationState = location.state as LocationWithNextRouteInState;
      if ("nextRoute" in locationState) {
        setNextRoute(locationState.nextRoute);
      }
    }
  }, [location.state]);

  useEffect(() => {
    if (pharmacyResults && showMap) {
      stickybits(".SelectPharmaciesPage__pharmaciesMap");
    }
  }, [pharmacyResults, showMap]);

  useEffect(() => {
    setSavedPharmacies(pharmacies);
  }, [pharmacies]);

  const showMapBasedOnResize = (): void => {
    const largeWidthMatch = window.matchMedia("(min-width: 1024px)");

    if (largeWidthMatch.matches) {
      setShowMap(true);
    }
  };

  const lisToSend =
    year !== mctCurrentPlanYear.toString() && futureLis ? futureLis : lis;

  const fetchPlan = async (planId: string): Promise<void> => {
    setPlanDetailState({
      status: PlanDetailsStatus.Loading,
      plan: undefined,
    });
    try {
      const fetchedPlan = await getPlan(
        planId,
        lisToSend ? { lis: lisToSend } : {}
      );
      setPlanDetailState({
        status: PlanDetailsStatus.Success,
        plan: fetchedPlan,
      });
    } catch (err) {
      setPlanDetailState({
        status: PlanDetailsStatus.Error,
        plan: undefined,
        error: err as Error,
      });
    }
  };

  const loadPharmacies = async (
    params: {
      radius_miles?: number;
      pageNumber?: number;
      calculateAverageDistance?: boolean;
      filterByPreferred?: boolean;
    } = {}
  ): Promise<void> => {
    setLoading(true);
    if (zipcode) {
      window.scrollTo(0, 0);

      try {
        const trimmedAddress = addressSearch.trim();
        const trimmedPharmName = pharmacyNameSearch.trim();
        const addressSearchIsZip = trimmedAddress.match(/^\d{5}$/gm);
        const pageNum =
          params.pageNumber !== undefined ? params.pageNumber : page;

        const pharmaciesRequest: PharmaciesRequest = {
          zipcode: addressSearchIsZip ? trimmedAddress : zipcode,
          page: pageNum,
          count: CONSTANTS.pharmacySelection.pageCount,
          name: trimmedPharmName,
          address: addressSearchIsZip ? "" : trimmedAddress,
          radius_miles: params.radius_miles || searchDistance,
        };

        if (isInNetwork && params.filterByPreferred) {
          pharmaciesRequest.preferred_only = params.filterByPreferred;
        }

        const response = isInNetwork
          ? await getInNetworkPharmacies(planId, pharmaciesRequest)
          : await getPharmacies(pharmaciesRequest);

        const {
          pharmacies: pharmaciesResponse,
          total_results,
          source_address,
          geocode_error,
        } = response;

        if (isInNetwork) {
          const {
            mail_order_network,
            mail_order_preferred,
          } = response as InNetworkPharmaciesResponse;
          setMailOrderNetworkStatus({
            isMailOrderInNetwork: mail_order_network,
            isMailOrderPreferred: mail_order_preferred,
            shouldMailOrderBeInResults: params.filterByPreferred
              ? mail_order_preferred
              : mail_order_network,
          });
        }

        dispatch({
          type: ActionType.SET_PHARMACIES,
          payload: updatePharmacyNetworkStatuses(
            pharmaciesResponse.features,
            savedPharmacies
          ),
        });

        setPharmacyResults({
          pharmacies: pharmaciesResponse,
          sourceAddress: source_address,
          geoError: geocode_error,
          totalResults: total_results,
        });

        setTotalPharmacies(total_results);
        setTotalPages(
          Math.ceil(total_results / CONSTANTS.pharmacySelection.pageCount)
        );
        const builtAddress = buildAddress(source_address);
        setAddressSearch(builtAddress);
        dispatch({
          type: ActionType.UPDATE_PHARMACY_ADDRESS,
          payload: builtAddress,
        });
        setPreviousNameSearch(pharmacyNameSearch);
        setPage(pageNum);

        setLoading(false);

        if (pharmaciesResponse.features.length === 0) {
          sendTealiumEvent(
            "Find Plans - Add Pharmacy",
            `Error: ${t("pharm_selection.no_pharmacies")}`
          );
        }

        if (params.calculateAverageDistance) {
          const averageDistance = Math.ceil(
            pharmaciesResponse.features.reduce(
              (prev, curr) => prev + (curr.properties?.distance_miles || 0),
              0
            ) / pharmaciesResponse.features.length
          );

          let roundedDistance;

          if (averageDistance <= 1) {
            roundedDistance = 1;
          } else if (averageDistance <= 5) {
            roundedDistance = 5;
          } else if (averageDistance <= 10) {
            roundedDistance = 10;
          } else {
            roundedDistance = 25;
          }

          setSearchDistance(roundedDistance);
          loadPharmacies({
            pageNumber: 0,
            radius_miles: roundedDistance,
            filterByPreferred: Boolean(params.filterByPreferred),
          });
          if (!isInNetwork) {
            sendTealiumEvent(
              "Find Plans - Add Pharmacy",
              `Results UI: Filter by: ${roundedDistance} miles - Default`
            );
          }
        }
      } catch (e) {
        setLoading(false);
      }
    }
  };

  /**
   * toggleMailOrderPharmacy is called when the user check/uncheck mail-order pharmacy
   * @param add true to add mail-order pharmacy, false to remove mail-order pharmacy
   */
  const toggleMailOrderPharmacy = (add: boolean) => {
    // we need to dispatch two actions
    //  1. UPDATE_PHARMACY_TYPE
    //    - update the PharmacyType stored in AppStore.
    if (add) {
      dispatch({
        type: ActionType.UPDATE_PHARMACY_TYPE,
        payload: PharmacyType.MAIL_AND_RETAIL,
      });

      if (!isInNetwork) {
        // Add Mail Order Pharmacy - Regular Pharmacy
        sendTealiumEvent(
          "Find Plans - Add Pharmacy",
          "Mail Order Pharmacy|Added"
        );

        createGa4Event({
          event_name: Ga4Event.PHARMACY_SELECTION,
          state,
          flags,
          mct_pharmacy_name: "Mail Order Pharmacy",
          mct_pharmacy_type: "mail",
        });
      } else {
        // Add Mail Order Pharmacy - In-Network Pharmacy
        const selectedPharmacyCount = pharmacies.length + 1;

        sendTealiumEvent(
          "Find Plans - Add Pharmacy",
          `added|mail order|Mail Order Pharmacy|${selectedPharmacyCount}`
        );
        createGa4Event({
          event_name: Ga4Event.IN_NETWORK_PHARMACY_SELECTED,
          state,
          flags,
          mct_pharmacy_name: "Mail Order Pharmacy",
          mct_pharmacy_type: "mail",
          mct_in_network_pharmacy_finder: "yes",
          pharmacy_count: selectedPharmacyCount,
        });
      }
    } else {
      dispatch({
        type: ActionType.UPDATE_PHARMACY_TYPE,
        payload: PharmacyType.RETAIL,
      });

      if (!isInNetwork) {
        // Remove Mail Order Pharmacy - Regular Pharmacy
        sendTealiumEvent(
          "Find Plans - Add Pharmacy",
          "Mail Order Pharmacy|Removed"
        );

        createGa4Event({
          event_name: Ga4Event.PHARMACY_REMOVED,
          state,
          flags,
          mct_pharmacy_name: "Mail Order Pharmacy",
          mct_pharmacy_type: "mail",
        });
      } else {
        // Remove Mail Order Pharmacy - In-Network Pharmacy
        const selectedPharmacyCount = pharmacies.length;

        sendTealiumEvent(
          "Find Plans - Add Pharmacy",
          `removed|mail order|Mail Order Pharmacy|${selectedPharmacyCount}`
        );

        createGa4Event({
          event_name: Ga4Event.PHARMACY_REMOVED,
          state,
          flags,
          mct_pharmacy_name: "Mail Order Pharmacy",
          mct_pharmacy_type: "mail",
          mct_in_network_pharmacy_finder: "yes",
          pharmacy_count: selectedPharmacyCount,
        });
      }
    }
    setMailOrder(add);
  };

  const togglePharmacy = (toggledFrom = ToggledFrom.toggledFromList) => (
    npi: string | number,
    pharmacyNumber = 0
  ): void => {
    const castedNpi = Number(npi).toString();
    if (pharmacyResults) {
      const npis = pharmacies.map(p => p.npi);
      const pharmacy = pharmacyResults.pharmacies.features.find(
        pgp => pgp.properties.npi === npi
      );
      const pharmacyName = pharmacy?.properties?.name || "";

      if (!npis.includes(castedNpi)) {
        if (
          CONSTANTS.pharmacySelection.max === pharmacies.length ||
          (pharmacies.length === CONSTANTS.pharmacySelection.max - 1 &&
            mailOrder)
        ) {
          setFooterMessage(
            fm(
              {
                id: "pharmacy_selection_page.max_selection_warning",
              },
              { count: CONSTANTS.pharmacySelection.max }
            )
          );
          return;
        }

        const pharmacy = pharmacyResults.pharmacies.features.find(
          feature => feature.properties?.npi === npi
        );

        dispatch({
          type: ActionType.ADD_PHARMACY,
          payload: pharmacy?.properties,
        });

        // Analytics
        if (!isInNetwork) {
          // Add Retail Pharmacy - Regular Pharmacy
          sendTealiumEvent(
            "Find Plans - Add Pharmacy",
            `Retail|${pharmacyNumber}|${pharmacyName}|Added`
          );

          createGa4Event({
            event_name: Ga4Event.PHARMACY_SELECTION,
            state,
            flags,
            mct_pharmacy_name: pharmacyName,
            mct_pharmacy_type: "retail",
            search_result_count: pharmacyResults.totalResults,
            search_result_position: pharmacyNumber,
          });
        } else {
          // Add Retail Pharmacy - In-Network Pharmacy
          const selectedPharmacyCount = mailOrder
            ? pharmacies.length + 2
            : pharmacies.length + 1;

          sendTealiumEvent(
            "Find Plans - Add Pharmacy",
            `added retail ${pharmacyName} ${selectedPharmacyCount}`
          );
          createGa4Event({
            event_name: Ga4Event.IN_NETWORK_PHARMACY_SELECTED,
            state,
            flags,
            mct_pharmacy_name: pharmacyName,
            mct_pharmacy_type: "retail",
            mct_in_network_pharmacy_finder: "yes",
            pharmacy_count: selectedPharmacyCount,
          });
        }
      } else {
        // Remove Retail Pharmacy
        dispatch({ type: ActionType.DELETE_PHARMACY, payload: npi });

        // Analytics
        if (!isInNetwork) {
          // Remove Retail Pharmacy - Regular Pharmacy
          sendTealiumEvent(
            "Find Plans - Add Pharmacy",
            `Retail|${pharmacyNumber}|${pharmacyName}|Removed`
          );
          createGa4Event({
            event_name: Ga4Event.PHARMACY_REMOVED,
            state,
            flags,
            mct_pharmacy_name: pharmacyName,
            mct_pharmacy_type: "retail",
          });
        } else {
          // Remove Retail Pharmacy - In-Network Pharmacy
          const selectedPharmacyCount = mailOrder
            ? pharmacies.length
            : pharmacies.length - 1;

          sendTealiumEvent(
            "Find Plans - Add Pharmacy",
            `removed retail ${pharmacyName} ${selectedPharmacyCount}`
          );
          createGa4Event({
            event_name: Ga4Event.PHARMACY_REMOVED,
            state,
            flags,
            mct_pharmacy_name: pharmacyName,
            mct_pharmacy_type: "retail",
            mct_in_network_pharmacy_finder: "yes",
            pharmacy_count: selectedPharmacyCount,
          });
        }

        if (toggledFrom === ToggledFrom.toggledFromTray) {
          sendTealiumEvent(
            "Find Plans - Add Pharmacy",
            `Remove Pharmacy from Top 5`
          );
        }
      }
    }
  };

  const handleRemoveMailOrder = (): void => {
    toggleMailOrderPharmacy(false);
  };

  useEffect(() => {
    if (isInNetwork) {
      if (planId) {
        loadPharmacies({
          pageNumber: 0,
          radius_miles: DEFAULT_SEARCH_DISTANCE,
          calculateAverageDistance: true,
        });
      }
    } else {
      loadPharmacies({
        pageNumber: 0,
        radius_miles: DEFAULT_SEARCH_DISTANCE,
        calculateAverageDistance: true,
      });
    }
  }, [zipcode, planId, isInNetwork]);

  useEffect(() => {
    setMobileSearchDistance(showMobileFilter ? searchDistance : undefined);
  }, [showMobileFilter]);

  useEffect(() => {
    if (footerMessage !== "") {
      setFooterMessage("");
    }
  }, [footerMessage]);

  const inNetworkFilterAnalytics = (
    showPreferred: boolean,
    searchDistance: number
  ) => {
    if (isInNetwork) {
      sendTealiumEvent(
        "Find Plans - Add Pharmacy",
        `Results UI: Filter by: Distance ${searchDistance} miles. Show only preferred in-network pharmacies: ${
          showPreferred ? "yes" : "no"
        }`
      );
      createGa4Event({
        event_name: Ga4Event.RESULTS_FILTERED,
        click_text: "Show only preferred in-network pharmacies",
        state,
        flags,
        radio_button_text: `Distance ${searchDistance} mile`,
        filter_action: showPreferred ? "applied" : "removed",
      });
    }
  };

  const handlePreferredOnlyClick = (showPreferred: boolean) => {
    loadPharmacies({ pageNumber: 0, filterByPreferred: showPreferred });
    setShowOnlyPreferred(showPreferred);
    inNetworkFilterAnalytics(showPreferred, searchDistance);
  };

  const constructBackButtonRoute = () => {
    const { search } = location;
    if (isInNetwork) {
      return nextRoute
        ? (nextRoute as string)
        : `${routes.searchResults}${search}`;
    } else {
      return routes.managePrescriptions;
    }
  };

  const handleOnBackButtonClick = (event: MouseEvent) => {
    if (isInNetwork) {
      if (!savedPharmacies.length && !mailOrder) {
        event.preventDefault();
        setFooterMessage(t("pharmacy_selection_page.no_pharmacies_selected"));
      } else {
        sendTealiumEvent("Find Plans - Add Pharmacy", "Back to plan details");
        createGa4Event({
          event_name: Ga4Event.IN_NETWORK_PHARMACY_BACK_TO_PLAN_DETAILS,
          click_text: "Back to plan details",
          state,
          flags,
        });
      }
    } else {
      sendTealiumEvent("Find Plans - Add Pharmacy", "Back to drug selection");
    }
  };

  // heading is the shown on the page, i.e. <h1>.
  // title is the page title.
  const heading = isInNetwork
    ? t("pharm_selection.in_network.in_network_pharamcy_finder")
    : t("pharm_selection.choose_5");
  const yearInTitle = useYearInTitle(heading, state.year);
  const title = isInNetwork ? heading : yearInTitle;

  return (
    <>
      <AppPage title={title}>
        <>
          {!hasMbpReferrerUri && (
            <Link
              to={constructBackButtonRoute()}
              onClick={handleOnBackButtonClick}
              className="SelectPharmaciesPage__backButton"
            >
              <span className="fa fa-chevron-left ds-u-margin-right--1" />
              {isInNetwork
                ? t("pharm_selection.back_to_plan_details")
                : t("pharm_selection.back_to_drug_selection")}
            </Link>
          )}
          {isInNetwork && planDetailState.plan && (
            <h2 className="ds-text-heading--lg ds-u-margin-bottom--0">
              {planDetailState.plan.name
                ? getPlanName(planDetailState.plan, state.language)
                : ""}
            </h2>
          )}
          <FocusableHeader
            className={`ds-u-margin-bottom--1 ${
              isInNetwork ? "ds-u-margin-top--0" : "ds-u-margin-top--3"
            }`}
            text={heading}
          />
          {!isInNetwork && (
            <p className="SelectPharmaciesPage__desc">
              {t("pharm_selection.drug_costs")}
            </p>
          )}

          <form
            onSubmit={(e): void => {
              e.preventDefault();

              if (zipcode) {
                loadPharmacies({
                  pageNumber: 0,
                  radius_miles: searchDistance,
                  calculateAverageDistance: true,
                  filterByPreferred: showOnlyPreferred,
                });
              }
            }}
            className="ds-l-container ds-u-padding-x--0 ds-u-margin-x--0 ds-u-margin-top--1 ds-u-lg-margin-top--3"
          >
            <div className="ds-l-form-row ds-u-align-items--end">
              <div
                className={`ds-l-col--12 ${
                  isInNetwork ? "ds-l-lg-col--9" : "ds-l-lg-col--5"
                }`}
              >
                <TextField
                  className="SelectPharmaciesPage__zipInput e2e-pharm-zip"
                  fieldClassName="mct-u-max-width--full"
                  name="pharm-address"
                  value={addressSearch}
                  label={t("pharm_selection.pharm_address_input")}
                  onFocus={(): void => {
                    sendTealiumEvent(
                      "Find Plans - Add Pharmacy",
                      "UI Form: Enter your zip code or street address"
                    );
                  }}
                  onChange={(event: ChangeEvent<HTMLInputElement>): void => {
                    setAddressSearch(event.target.value);
                  }}
                />
              </div>
              {!isInNetwork && (
                <div className="ds-l-col--12 ds-l-lg-col--4">
                  <TextField
                    label={t("pharm_selection.pharm_name_input")}
                    name="pharm-name"
                    value={pharmacyNameSearch}
                    className="SelectPharmaciesPage__pharmNameInput ds-u-margin-bottom--0"
                    fieldClassName="mct-u-max-width--full"
                    onFocus={(): void => {
                      sendTealiumEvent(
                        "Find Plans - Add Pharmacy",
                        "UI Form: Name of pharmacy"
                      );
                    }}
                    onChange={(event: ChangeEvent<HTMLInputElement>): void => {
                      setPharmacyNameSearch(event.target.value);
                    }}
                  />
                </div>
              )}
              <div className="ds-l-col--12 ds-l-lg-col--3 ds-u-margin-top--3 ds-u-lg-margin-top--0">
                <Button
                  type="submit"
                  variation="primary"
                  className="SelectPharmaciesPage__searchButton mct-u-min-width--full ds-u-padding--2"
                  onClick={(): void => {
                    sendTealiumEvent(
                      "Find Plans - Add Pharmacy",
                      "CTA: Find Pharmacy"
                    );
                  }}
                >
                  {t("pharm_selection.find_pharmacy")}
                </Button>
              </div>
            </div>
          </form>
          <ResultsContainer
            {...{
              handlePreferredOnlyClick,
              isInNetwork,
              isMailOrderInNetwork: mailOrderNetworkStatus.isMailOrderInNetwork,
              isMailOrderPreferred: mailOrderNetworkStatus.isMailOrderPreferred,
              loading,
              loadPharmacies,
              mailOrder,
              mobileSearchDistance,
              page,
              pharmacies,
              pharmacyAnchorTarget,
              pharmacyResults,
              previousNameSearch,
              searchDistance,
              setFooterMessage,
              setMobileSearchDistance,
              setPage,
              setSearchDistance,
              setShowMap,
              setShowMobileFilter,
              showMap,
              showMobileFilter,
              shouldMailOrderBeInResults:
                mailOrderNetworkStatus.shouldMailOrderBeInResults,
              toggleMailOrderPharmacy,
              togglePharmacy,
              totalPages,
              totalPharmacies,
              inNetworkFilterAnalytics,
            }}
          />
          <LoadingMask loading={loading} delay={500} />
        </>
      </AppPage>
      <PharmacySelectionFooter
        selectionList={savedPharmacies}
        onRemovePharmacyFromSelection={togglePharmacy(
          ToggledFrom.toggledFromTray
        )}
        hasMailOrder={mailOrder}
        onRemoveMailOrder={handleRemoveMailOrder}
        pageLoading={loading}
        warningMessage={footerMessage}
        nextRoute={nextRoute}
        isInNetwork={isInNetwork}
        isMailOrderInNetwork={mailOrderNetworkStatus.isMailOrderInNetwork}
        isMailOrderPreferred={mailOrderNetworkStatus.isMailOrderPreferred}
      />
    </>
  );
};
